import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import SupportThread from '../../components/SupportThread';

const NAVY      = '#0D1B2A';
const NAVY_MID  = '#132233';
const AMBER     = '#F5A623';
const WHITE     = '#FFFFFF';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";

const ACCOUNT_TYPES = [
  { key: 'worker',       labelKey: 'support.accountTypeWorker',       searchPlaceholderKey: 'support.searchWorkersPlaceholder' },
  { key: 'employer',     labelKey: 'support.accountTypeEmployer',     searchPlaceholderKey: 'support.searchEmployersPlaceholder' },
  { key: 'union',        labelKey: 'support.accountTypeUnion',        searchPlaceholderKey: 'support.searchUnionsPlaceholder' },
  { key: 'organization', labelKey: 'support.accountTypeOrganization', searchPlaceholderKey: 'support.searchOrganizationsPlaceholder' },
];

const TYPE_COLOR = { worker: '#60A5FA', employer: AMBER, union: '#A78BFA', organization: '#34D399' };

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return i18n.t('messaging.now');
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

function initialsFor(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return (parts[0]?.[0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '');
}

export default function AdminSupportMessages({ supabase, adminUser, onUnreadCountChange }) {
  const { t } = useTranslation();
  const accountTypeLabel = (type) => {
    const cfg = ACCOUNT_TYPES.find(a => a.key === type);
    return cfg ? t(cfg.labelKey) : type;
  };
  const [conversations, setConversations] = useState([]);
  const [loadingConvs, setLoadingConvs]   = useState(true);
  const [activeId, setActiveId]           = useState(null);
  const [statusFilter, setStatusFilter]   = useState('all');
  const [nameSearch, setNameSearch]       = useState('');
  const [isMobile, setIsMobile]           = useState(window.innerWidth < 640);
  const [mobileView, setMobileView]       = useState('list');

  const [showNewModal, setShowNewModal]   = useState(false);
  const [pickerType, setPickerType]       = useState('worker');
  const [pickerQuery, setPickerQuery]     = useState('');
  const [pickerResults, setPickerResults] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerBusyId, setPickerBusyId]   = useState(null);

  const loadingRef = useRef(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const loadConversations = async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    const { data: convRows } = await supabase
      .from('support_conversations')
      .select('*')
      .order('updated_at', { ascending: false });
    const rows = convRows || [];

    const idsByType = { worker: [], employer: [], union: [], organization: [] };
    rows.forEach(r => idsByType[r.account_type]?.push(r.account_id));

    const [workersRes, employersRes, unionsRes, orgsRes] = await Promise.all([
      idsByType.worker.length       ? supabase.from('workers').select('id, first_name, last_name, email').in('id', idsByType.worker)             : Promise.resolve({ data: [] }),
      idsByType.employer.length     ? supabase.from('employers').select('id, company_name, email').in('id', idsByType.employer)                   : Promise.resolve({ data: [] }),
      idsByType.union.length        ? supabase.from('union_accounts').select('id, union_name, email').in('id', idsByType.union)                   : Promise.resolve({ data: [] }),
      idsByType.organization.length ? supabase.from('organizations').select('id, org_name').in('id', idsByType.organization)                      : Promise.resolve({ data: [] }),
    ]);

    const nameMap = {};
    (workersRes.data   || []).forEach(w => { nameMap[`worker:${w.id}`]       = `${w.first_name || ''} ${w.last_name || ''}`.trim() || w.email || 'Worker'; });
    (employersRes.data || []).forEach(e => { nameMap[`employer:${e.id}`]     = e.company_name || e.email || 'Employer'; });
    (unionsRes.data     || []).forEach(u => { nameMap[`union:${u.id}`]       = u.union_name || u.email || 'Union'; });
    (orgsRes.data       || []).forEach(o => { nameMap[`organization:${o.id}`] = o.org_name || 'Organization'; });

    const convIds = rows.map(r => r.id);
    const msgsByConv = {};
    const unreadByConv = {};
    if (convIds.length > 0) {
      const { data: allMsgs } = await supabase
        .from('support_messages')
        .select('*')
        .in('conversation_id', convIds)
        .order('created_at', { ascending: true });
      (allMsgs || []).forEach(m => {
        msgsByConv[m.conversation_id] = m; // ascending order -> last write wins = latest message
        if (m.sender_type === 'account' && !m.read_at) {
          unreadByConv[m.conversation_id] = (unreadByConv[m.conversation_id] || 0) + 1;
        }
      });
    }

    const enriched = rows.map(r => ({
      ...r,
      accountName: nameMap[`${r.account_type}:${r.account_id}`] || 'Unknown account',
      lastMessage: msgsByConv[r.id] || null,
      unreadCount: unreadByConv[r.id] || 0,
    }));

    setConversations(enriched);
    setLoadingConvs(false);
    onUnreadCountChange?.(enriched.reduce((sum, c) => sum + c.unreadCount, 0));
    loadingRef.current = false;
  };

  useEffect(() => { loadConversations(); }, []); // eslint-disable-line

  // Admin sees every support conversation (is_admin() RLS), so a broad, unfiltered
  // subscription is enough -- keep it simple and just refetch on any change.
  useEffect(() => {
    const convChannel = supabase
      .channel('admin-support-conversations')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'support_conversations' }, () => loadConversations())
      .subscribe();
    const msgChannel = supabase
      .channel('admin-support-messages')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'support_messages' }, () => loadConversations())
      .subscribe();
    return () => { supabase.removeChannel(convChannel); supabase.removeChannel(msgChannel); };
  }, []); // eslint-disable-line

  // Picker search (debounced)
  useEffect(() => {
    if (!showNewModal) return;
    setPickerLoading(true);
    const t = setTimeout(async () => {
      const q = pickerQuery.trim();
      let query;
      if (pickerType === 'worker')       query = supabase.from('workers').select('id, first_name, last_name, email');
      else if (pickerType === 'employer') query = supabase.from('employers').select('id, company_name, email');
      else if (pickerType === 'union')    query = supabase.from('union_accounts').select('id, union_name, email');
      else                                 query = supabase.from('organizations').select('id, org_name');

      // Match per word (each chained .or()/.ilike() call ANDs with the rest) so a
      // full-name query like "Sam Supportee" still matches split first/last-name columns.
      if (q) {
        q.split(/\s+/).filter(Boolean).forEach(word => {
          if (pickerType === 'worker')       query = query.or(`first_name.ilike.%${word}%,last_name.ilike.%${word}%,email.ilike.%${word}%`);
          else if (pickerType === 'employer') query = query.or(`company_name.ilike.%${word}%,email.ilike.%${word}%`);
          else if (pickerType === 'union')    query = query.or(`union_name.ilike.%${word}%,email.ilike.%${word}%`);
          else                                 query = query.ilike('org_name', `%${word}%`);
        });
      }
      const { data } = await query.limit(20);
      setPickerResults(data || []);
      setPickerLoading(false);
    }, 300);
    return () => clearTimeout(t);
  }, [pickerQuery, pickerType, showNewModal]); // eslint-disable-line

  const handlePickAccount = async (id) => {
    setPickerBusyId(id);
    const { data: existing } = await supabase
      .from('support_conversations')
      .select('id')
      .eq('account_type', pickerType)
      .eq('account_id', id)
      .eq('status', 'open')
      .maybeSingle();

    let convId = existing?.id;
    if (!convId) {
      const { data: created, error } = await supabase
        .from('support_conversations')
        .insert({ account_type: pickerType, account_id: id })
        .select('id')
        .single();
      if (error) {
        if (error.code === '23505') {
          // Lost a true-parallel race to the DB's one-open-conversation-per-account
          // constraint -- the other insert won, so just fetch and use that one.
          const { data: winner } = await supabase
            .from('support_conversations')
            .select('id')
            .eq('account_type', pickerType)
            .eq('account_id', id)
            .eq('status', 'open')
            .maybeSingle();
          convId = winner?.id;
        }
        if (!convId) { alert(`${t('support.couldNotStartConversationPrefix')}${error.message}`); setPickerBusyId(null); return; }
      } else {
        convId = created.id;
      }
    }

    setPickerBusyId(null);
    setShowNewModal(false);
    setPickerQuery('');
    setActiveId(convId);
    setMobileView('thread');
    loadConversations();
  };

  const toggleStatus = async () => {
    const conv = conversations.find(c => c.id === activeId);
    if (!conv) return;
    const nextStatus = conv.status === 'open' ? 'closed' : 'open';
    await supabase.from('support_conversations').update({ status: nextStatus }).eq('id', activeId);
    loadConversations();
  };

  const filtered = conversations
    .filter(c => statusFilter === 'all' ? true : c.status === statusFilter)
    .filter(c => nameSearch.trim() ? c.accountName.toLowerCase().includes(nameSearch.trim().toLowerCase()) : true);
  const activeConv = conversations.find(c => c.id === activeId);
  const showList   = !isMobile || mobileView === 'list';
  const showThread = !isMobile || mobileView === 'thread';

  const panelHeader = {
    height: '52px', display: 'flex', alignItems: 'center', padding: '0 16px', gap: '10px',
    backgroundColor: NAVY, borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0,
  };

  return (
    <div style={{ display: 'flex', height: '100%', fontFamily: FONT_BODY, backgroundColor: NAVY_MID, overflow: 'hidden' }}>

      {/* ── Left: conversation list ── */}
      {showList && (
        <div style={{
          width: isMobile ? '100%' : '320px', flexShrink: 0,
          borderRight: isMobile ? 'none' : '1px solid rgba(255,255,255,0.08)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          <div style={panelHeader}>
            <span style={{ fontFamily: FONT_HEAD, fontSize: '1.5rem', color: WHITE, letterSpacing: '0.06em', flex: 1 }}>
              {t('support.panelTitle')}
            </span>
            <button
              onClick={() => setShowNewModal(true)}
              style={{
                backgroundColor: AMBER, color: NAVY, border: 'none', borderRadius: '16px',
                padding: '5px 12px', fontSize: '12px', fontWeight: 700, fontFamily: FONT_BODY, cursor: 'pointer',
              }}
            >
              {t('support.newButton')}
            </button>
          </div>

          <div style={{ padding: '10px 12px', backgroundColor: '#0b1621', borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}>
            <div style={{ position: 'relative' }}>
              <svg
                width="13" height="13" viewBox="0 0 24 24" fill="none"
                stroke="rgba(255,255,255,0.35)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
              >
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                type="text"
                value={nameSearch}
                onChange={e => setNameSearch(e.target.value)}
                placeholder={t('support.searchByNamePlaceholder')}
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '7px 12px 7px 30px',
                  fontSize: '12px', fontFamily: FONT_BODY,
                  background: NAVY_MID, border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '8px', color: WHITE, outline: 'none',
                }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.07)', backgroundColor: '#0b1621', flexShrink: 0 }}>
            {[
              { value: 'all',    labelKey: 'support.filterAll' },
              { value: 'open',   labelKey: 'support.filterOpen' },
              { value: 'closed', labelKey: 'support.filterClosed' },
            ].map(f => (
              <button
                key={f.value}
                onClick={() => setStatusFilter(f.value)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  padding: '8px 14px', fontSize: '0.76rem', fontWeight: 600, textTransform: 'capitalize',
                  fontFamily: FONT_BODY,
                  color: statusFilter === f.value ? AMBER : 'rgba(255,255,255,0.4)',
                  borderBottom: statusFilter === f.value ? `2px solid ${AMBER}` : '2px solid transparent',
                  marginBottom: '-1px',
                }}
              >
                {t(f.labelKey)}
              </button>
            ))}
          </div>

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loadingConvs ? (
              <div style={{ padding: '24px', textAlign: 'center', color: 'rgba(255,255,255,0.35)', fontSize: '13px' }}>{t('common.loading')}</div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: '32px 20px', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '13px', lineHeight: 1.6 }}>
                {nameSearch.trim()
                  ? t('support.emptyNoMatchSearch')
                  : statusFilter !== 'all'
                    ? t('support.emptyNoConversationsFiltered', { status: statusFilter === 'open' ? t('support.filterOpen') : t('support.filterClosed') })
                    : t('support.emptyNoConversations')}
              </div>
            ) : filtered.map(conv => {
              const isActive = conv.id === activeId;
              const lm = conv.lastMessage;
              const isUnread = conv.unreadCount > 0;
              const preview = lm ? (lm.sender_type === 'admin' ? t('messaging.youPrefix') : '') + lm.content : t('messaging.noMessagesYetShort');
              return (
                <div
                  key={conv.id}
                  onClick={() => { setActiveId(conv.id); setMobileView('thread'); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '12px 16px', cursor: 'pointer',
                    backgroundColor: isActive ? 'rgba(245,166,35,0.07)' : isUnread ? 'rgba(255,255,255,0.05)' : 'transparent',
                    borderLeft: `3px solid ${isActive ? AMBER : 'transparent'}`,
                    borderBottom: '1px solid rgba(255,255,255,0.05)',
                    opacity: conv.status === 'closed' ? 0.6 : 1,
                  }}
                >
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                    backgroundColor: TYPE_COLOR[conv.account_type] || AMBER,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '13px', fontWeight: 700, color: NAVY,
                  }}>
                    {initialsFor(conv.accountName)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '2px' }}>
                      <span style={{ fontSize: '13px', fontWeight: isUnread ? 700 : 600, color: WHITE, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {conv.accountName}
                      </span>
                      {lm && (
                        <span style={{ fontSize: '11px', color: isUnread ? AMBER : 'rgba(255,255,255,0.3)', flexShrink: 0, marginLeft: '8px' }}>
                          {timeAgo(lm.created_at)}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{
                        fontSize: '9.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
                        color: TYPE_COLOR[conv.account_type] || AMBER, flexShrink: 0,
                      }}>
                        {accountTypeLabel(conv.account_type)}
                      </span>
                      <span style={{ fontSize: '12px', color: isUnread ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.38)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: isUnread ? 600 : 400 }}>
                        {preview}
                      </span>
                    </div>
                  </div>
                  {isUnread && (
                    <span style={{
                      backgroundColor: AMBER, color: NAVY, borderRadius: '999px', fontSize: '11px', fontWeight: 700,
                      padding: '2px 7px', lineHeight: 1.4, flexShrink: 0,
                    }}>
                      {conv.unreadCount}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Right: thread ── */}
      {showThread && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {!activeConv ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '14px' }}>
              <span style={{ fontSize: '40px' }}>🛟</span>
              <p style={{ color: 'rgba(255,255,255,0.25)', fontSize: '14px' }}>{t('support.selectConversationPrompt')}</p>
            </div>
          ) : (
            <SupportThread
              supabase={supabase}
              conversationId={activeConv.id}
              viewerType="admin"
              viewerId={adminUser.id}
              title={activeConv.accountName}
              subtitle={`${accountTypeLabel(activeConv.account_type)} · ${activeConv.status === 'open' ? t('support.statusOpen') : t('support.statusClosed')}`}
              avatarLabel={initialsFor(activeConv.accountName)}
              avatarColor={TYPE_COLOR[activeConv.account_type] || AMBER}
              onBack={isMobile ? () => setMobileView('list') : undefined}
              headerRight={
                <button
                  onClick={toggleStatus}
                  style={{
                    background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)',
                    color: 'rgba(255,255,255,0.7)', fontSize: '11px', fontWeight: 700, padding: '5px 10px',
                    borderRadius: '6px', cursor: 'pointer', fontFamily: FONT_BODY, flexShrink: 0, whiteSpace: 'nowrap',
                  }}
                >
                  {activeConv.status === 'open' ? t('support.markClosedButton') : t('support.reopenButton')}
                </button>
              }
            />
          )}
        </div>
      )}

      {/* ── New conversation modal ── */}
      {showNewModal && (
        <div
          onClick={e => { if (e.target === e.currentTarget) setShowNewModal(false); }}
          style={{ position: 'fixed', inset: 0, zIndex: 1000, backgroundColor: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
        >
          <div style={{
            backgroundColor: NAVY, borderRadius: '14px', border: '1px solid rgba(255,255,255,0.12)',
            padding: '24px', width: '100%', maxWidth: '440px', maxHeight: '80vh',
            display: 'flex', flexDirection: 'column', gap: '14px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 style={{ fontFamily: FONT_HEAD, fontSize: '1.7rem', color: WHITE, letterSpacing: '0.05em', margin: 0 }}>
                {t('support.newConversationHeading')}
              </h2>
              <button
                onClick={() => setShowNewModal(false)}
                style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: '20px', cursor: 'pointer', lineHeight: 1, padding: '4px' }}
              >
                ×
              </button>
            </div>

            <div style={{ display: 'flex', gap: '6px', backgroundColor: '#0b1621', borderRadius: '9px', padding: '4px' }}>
              {ACCOUNT_TYPES.map(at => (
                <button
                  key={at.key}
                  onClick={() => { setPickerType(at.key); setPickerResults([]); }}
                  style={{
                    flex: 1, textAlign: 'center', padding: '7px 4px', borderRadius: '6px', cursor: 'pointer',
                    fontSize: '0.76rem', fontWeight: 600, fontFamily: FONT_BODY, border: 'none',
                    backgroundColor: pickerType === at.key ? AMBER : 'transparent',
                    color: pickerType === at.key ? NAVY : 'rgba(255,255,255,0.55)',
                  }}
                >
                  {t(at.labelKey)}
                </button>
              ))}
            </div>

            <input
              autoFocus
              value={pickerQuery}
              onChange={e => setPickerQuery(e.target.value)}
              placeholder={t(ACCOUNT_TYPES.find(at => at.key === pickerType).searchPlaceholderKey)}
              style={{
                width: '100%', boxSizing: 'border-box', backgroundColor: 'rgba(255,255,255,0.07)',
                border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '9px 12px',
                color: WHITE, fontFamily: FONT_BODY, fontSize: '14px', outline: 'none',
              }}
            />

            <div style={{ flex: 1, overflowY: 'auto', minHeight: '200px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {pickerLoading ? (
                <div style={{ padding: '24px', textAlign: 'center', color: 'rgba(255,255,255,0.35)', fontSize: '13px' }}>{t('support.searchingEllipsis')}</div>
              ) : pickerResults.length === 0 ? (
                <div style={{ padding: '24px', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '13px' }}>{t('support.noMatches')}</div>
              ) : pickerResults.map(r => {
                const name = pickerType === 'worker' ? `${r.first_name || ''} ${r.last_name || ''}`.trim()
                  : pickerType === 'employer' ? r.company_name
                  : pickerType === 'union' ? r.union_name
                  : r.org_name;
                return (
                  <div
                    key={r.id}
                    onClick={() => pickerBusyId ? null : handlePickAccount(r.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 10px',
                      borderRadius: '8px', cursor: pickerBusyId ? 'default' : 'pointer',
                      backgroundColor: 'rgba(255,255,255,0.04)',
                      opacity: pickerBusyId && pickerBusyId !== r.id ? 0.5 : 1,
                    }}
                  >
                    <div style={{
                      width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                      backgroundColor: TYPE_COLOR[pickerType] || AMBER, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '11px', fontWeight: 700, color: NAVY,
                    }}>
                      {initialsFor(name || r.email)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: WHITE, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {name || t('support.unnamedAccount')}
                      </div>
                      {r.email && (
                        <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {r.email}
                        </div>
                      )}
                    </div>
                    {pickerBusyId === r.id && (
                      <span style={{ fontSize: '11px', color: AMBER, flexShrink: 0 }}>{t('support.startingEllipsis')}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
